import { useMemo, useState } from 'react';
import { IMPACT_META, type MarketEvent } from '@/mocks/marketCalendar';
import { minutesToLabel, toUtcMinutes } from '../calculations';
import { CurrencyFlag } from './ui';

type Node = { key: string; time: number; label: string; sub: string; kind: 'session' | 'event'; color: string; event?: MarketEvent };

export default function DayPlanner({
  events,
  watchList,
  toDisplay,
  onSelect,
}: {
  events: MarketEvent[];
  watchList: string[];
  toDisplay: (utc: number) => number;
  onSelect: (e: MarketEvent) => void;
}) {
  const [highImpact, setHighImpact] = useState(true);
  const [london, setLondon] = useState(true);
  const [newYork, setNewYork] = useState(true);
  const [myWatch, setMyWatch] = useState(false);

  const nodes = useMemo(() => {
    const list: Node[] = [];

    if (london) {
      list.push({ key: 'london', time: 480, label: 'London Open', sub: 'Phiên London bắt đầu', kind: 'session', color: '#f59e0b' });
    }
    if (newYork) {
      list.push({ key: 'newyork', time: 780, label: 'New York Open', sub: 'Phiên New York bắt đầu', kind: 'session', color: '#ec4899' });
    }

    for (const e of events) {
      if (highImpact && e.impact === 'high') {
        list.push({ key: e.id, time: toUtcMinutes(e.time), label: e.title, sub: `${e.currency} · ${IMPACT_META[e.impact].label}`, kind: 'event', color: IMPACT_META[e.impact].color, event: e });
      } else if (myWatch && watchList.includes(e.currency)) {
        list.push({ key: e.id, time: toUtcMinutes(e.time), label: e.title, sub: `${e.currency} · theo dõi`, kind: 'event', color: '#7c3aed', event: e });
      }
    }

    list.sort((a, b) => a.time - b.time);
    return list;
  }, [events, watchList, highImpact, london, newYork, myWatch]);

  const toggles = [
    { key: 'high', label: 'High Impact Events', checked: highImpact, set: setHighImpact },
    { key: 'london', label: 'London Session', checked: london, set: setLondon },
    { key: 'newyork', label: 'New York Session', checked: newYork, set: setNewYork },
    { key: 'watch', label: 'My Market Watch', checked: myWatch, set: setMyWatch },
  ];

  return (
    <section className="rounded-3xl border border-slate-200/80 bg-white p-6 md:p-8">
      <h2 className="font-display text-xl font-bold tracking-tight text-slate-900 md:text-2xl">
        Lập kế hoạch ngày giao dịch
      </h2>
      <p className="mt-1.5 text-sm text-slate-500">Chọn những yếu tố bạn muốn theo dõi để tạo dòng thời gian cá nhân.</p>

      <div className="mt-5 flex flex-wrap gap-2">
        {toggles.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => t.set(!t.checked)}
            className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold transition-all ${
              t.checked ? 'border-violet-600 bg-violet-50 text-violet-700' : 'border-slate-200 bg-white text-slate-500'
            }`}
          >
            <span className={`flex h-4 w-4 items-center justify-center rounded border ${t.checked ? 'border-violet-600 bg-violet-600 text-white' : 'border-slate-300'}`}>
              {t.checked && <i className="ri-check-line text-[10px]" />}
            </span>
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6 rounded-2xl border border-slate-200/80 bg-slate-50/60 p-5">
        <p className="mb-4 flex items-center gap-2 text-sm font-bold text-slate-800">
          <i className="ri-star-line text-violet-500" />
          My Market Day
        </p>

        {nodes.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400">Chọn ít nhất một mục để tạo kế hoạch trong ngày.</p>
        ) : (
          <div className="relative space-y-0">
            <div className="absolute bottom-4 left-[7px] top-4 w-px bg-slate-200" />
            {nodes.map((n) => (
              <div key={n.key} className="relative flex items-start gap-3 py-2">
                <span
                  className="relative z-10 mt-1 h-3.5 w-3.5 shrink-0 rounded-full ring-4 ring-slate-50"
                  style={{ backgroundColor: n.color }}
                />
                <div className="flex flex-1 flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="font-mono text-xs font-bold tabular-nums text-slate-800">
                    {minutesToLabel(toDisplay(n.time))}
                  </span>
                  {n.kind === 'session' ? (
                    <span className="text-sm font-semibold text-slate-700">{n.label}</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => n.event && onSelect(n.event)}
                      className="flex items-center gap-1.5 text-sm font-medium text-slate-700 transition-colors hover:text-violet-700"
                    >
                      {n.event && <CurrencyFlag code={n.event.currency} />}
                      {n.label}
                    </button>
                  )}
                  <span className="text-[11px] text-slate-400">{n.sub}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}